import React from 'react'
import { Box, IconButton, Modal, Typography } from '@mui/material'
import CloseIcon from '@mui/icons-material/Close'
import SearchRoundedIcon from '@mui/icons-material/SearchRounded'

const themeColors = {
  primary: '#232536',
  secondary: '#ffcf51',
  hover: '#3a3e5a',
}

const BORDER_COLOR = '#e0e0e0'

/**
 * NoResultsModal
 *
 * Shown when a search (after v2 and v1 fallback) returns zero results.
 * Matches the visual language of the card modals elsewhere in the app
 * (MUI Modal portal, left-accent border, sticky close button).
 *
 * Closes on:
 *   - Close icon (top-right header)
 *   - ESC key (native MUI Modal behavior, not disabled)
 *   - Backdrop click (native MUI Modal behavior)
 *
 * Props:
 *   - username: display name of the current user (falls back to "User")
 *   - searchQuery: the query string that produced no hits
 *   - scopeName: user-facing name of the filter scope (law / tax
 *       transparency / country) — quoted in the message
 *   - scopeNoun: the noun used in "Please check whether the correct
 *       {scopeNoun} is selected". Defaults to "law".
 */
const NoResultsModal = ({
  open,
  onClose,
  username,
  searchQuery,
  scopeName,
  scopeNoun = 'law',
}) => {
  const displayName =
    (typeof username === 'string' && username.trim()) || 'User'
  const trimmedQuery =
    typeof searchQuery === 'string' ? searchQuery.trim() : ''
  const trimmedScopeName =
    typeof scopeName === 'string' ? scopeName.trim() : ''

  return (
    <Modal
      open={open}
      onClose={onClose}
      aria-labelledby='no-results-modal-title'
      aria-describedby='no-results-modal-description'
    >
      <Box
        sx={{
          position: 'absolute',
          top: '50%',
          left: '50%',
          transform: 'translate(-50%, -50%)',
          // Responsive width: near-full on mobile, capped on desktop so the
          // copy breathes without stretching across ultra-wide monitors.
          width: {
            xs: '95vw',
            sm: '85vw',
            md: '70vw',
            lg: '60vw',
          },
          maxWidth: '760px',
          minWidth: { xs: 'auto', sm: '420px' },
          maxHeight: '90vh',
          overflow: 'hidden',
          borderRadius: '8px',
          backgroundColor: 'white',
          boxShadow: '0 4px 20px rgba(0,0,0,0.2)',
          borderLeft: `5px solid ${themeColors.primary}`,
          outline: 'none',
          display: 'flex',
          flexDirection: 'column',
        }}
      >
        {/* Header */}
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            padding: '12px 16px',
            borderBottom: `1px solid ${BORDER_COLOR}`,
            backgroundColor: '#f8f8f8',
          }}
        >
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 1,
              minWidth: 0,
            }}
          >
            <SearchRoundedIcon sx={{ color: themeColors.primary }} />
            {/*
              Title + keyboard-shortcut hint live in the same flex row.
              `flexWrap: wrap` promotes the hint onto its own line when the
              header can't fit both side by side, so we get:
                - wide header  →  "No results found  Press [Esc] to close"
                - narrow header →  "No results found"
                                   "Press [Esc] to close"
            */}
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                flexWrap: 'wrap',
                columnGap: 1.75,
                rowGap: 0.25,
                minWidth: 0,
              }}
            >
              <Typography
                id='no-results-modal-title'
                variant='h6'
                sx={{
                  fontWeight: 600,
                  color: themeColors.primary,
                  lineHeight: 1.2,
                  whiteSpace: 'nowrap',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                }}
              >
                No results found
              </Typography>
              <Box
                sx={{
                  // Hidden on xs since phones have no Esc key.
                  display: { xs: 'none', sm: 'flex' },
                  alignItems: 'center',
                  gap: 0.6,
                  color: 'rgba(35, 37, 54, 0.55)',
                  fontSize: '0.72rem',
                  fontWeight: 400,
                  lineHeight: 1.2,
                  whiteSpace: 'nowrap',
                }}
              >
                <Box component='span'>Press</Box>
                <Box
                  component='kbd'
                  sx={{
                    fontFamily: 'inherit',
                    fontSize: '0.68rem',
                    fontWeight: 600,
                    color: 'rgba(35, 37, 54, 0.8)',
                    backgroundColor: 'rgba(35, 37, 54, 0.06)',
                    border: '1px solid rgba(35, 37, 54, 0.18)',
                    borderBottom: '2px solid rgba(35, 37, 54, 0.18)',
                    borderRadius: '4px',
                    padding: '1px 6px',
                    lineHeight: 1.2,
                    letterSpacing: '0.02em',
                  }}
                >
                  Esc
                </Box>
                <Box component='span'>to close</Box>
              </Box>
            </Box>
          </Box>
          <IconButton
            onClick={onClose}
            aria-label='close no-results dialog'
            size='small'
            sx={{
              backgroundColor: 'white',
              border: `1px solid ${themeColors.primary}`,
              boxShadow: '0 2px 10px rgba(0,0,0,0.2)',
              '&:hover': {
                backgroundColor: '#f5f5f5',
              },
            }}
          >
            <CloseIcon fontSize='small' sx={{ color: themeColors.primary }} />
          </IconButton>
        </Box>

        {/* Body */}
        <Box
          sx={{
            padding: { xs: 2, sm: 3 },
            overflowY: 'auto',
            // Long unbroken strings (e.g. a pasted slug or a spammy "fffff…"
            // search term) would otherwise blow out the modal width. Force
            // wrapping so the popup stays within its configured width.
            wordBreak: 'break-word',
            overflowWrap: 'anywhere',
          }}
        >
          <Typography
            variant='body1'
            sx={{ color: themeColors.primary, lineHeight: 1.6 }}
          >
            Dear{' '}
            <Box component='span' sx={{ fontWeight: 600 }}>
              {displayName}
            </Box>
            ,
          </Typography>
          <Typography
            id='no-results-modal-description'
            variant='body1'
            sx={{ color: themeColors.primary, lineHeight: 1.6, mt: 2 }}
          >
            {trimmedQuery ? (
              <>
                No results were found for{' '}
                <Box component='span' sx={{ fontWeight: 600 }}>
                  &ldquo;{trimmedQuery}&rdquo;
                </Box>
                {trimmedScopeName ? (
                  <>
                    {' '}under{' '}
                    <Box component='span' sx={{ fontWeight: 600 }}>
                      &ldquo;{trimmedScopeName}&rdquo;
                    </Box>
                  </>
                ) : null}
                .{' '}
              </>
            ) : (
              'No results were found. '
            )}
            Please check whether the correct {scopeNoun} is selected, or try
            an alternate search phrase.
          </Typography>
        </Box>
      </Box>
    </Modal>
  )
}

export default NoResultsModal
